import React from 'react';
import { useCallback } from 'react';
import toast from 'react-hot-toast';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { usePluralization } from 'common/hooks';
import useUser from 'common/hooks/useUser';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { OfferWithSelectionAndAuthor } from 'modules/shared-menu/types';

import { useBatchUpdateOffers } from './useBatchUpdateOffers';
import { Offer } from '../../../types';

export const getAssetIdFromUrl = (imageUrl: string) => {
  return /\/assets\/([a-z0-9]+)$/.exec(imageUrl)?.[1];
};

export const useBulkImageEnhancement = () => {
  const { executeBatchUpdateOffers } = useBatchUpdateOffers();
  const { user } = useUser();
  const { selection, offers, updateSelectionData } = useSelectionData();
  const { FormattedPluralMessage } = usePluralization();

  const bulkUpdateOfferImage = useCallback(
    async (offersToUpdate: Offer[], updatedImagesMap: Map<String, String>) => {
      if (!user) {
        throw new Error('User is not authenticated');
      }

      const updatedOffers: Offer[] = [];

      offersToUpdate.forEach(offer => {
        if (!offer.images || offer.images.length < 1) {
          return;
        }

        let updatedImagesCounter: number = 0;

        const updatedImages = offer.images?.map(image => {
          const updatedImageUrl = updatedImagesMap.get(image.url) ?? null;

          if (!updatedImageUrl) {
            return image;
          }

          updatedImagesCounter++;

          return {
            url: String(updatedImageUrl),
          };
        });

        if (updatedImagesCounter > 0) {
          updatedOffers.push({ ...offer, images: updatedImages, image: updatedImages?.[0].url });
        }
      });

      const offerPayloads: OfferWithSelectionAndAuthor[] = updatedOffers.map(({ scope, ...offerPayload }) => ({
        ...offerPayload,
        selection_id: selection.id,
        author: {
          id: user.id,
          kind: 'user',
        },
      }));

      await executeBatchUpdateOffers({
        offerPayloads,
        onSuccess: () => {
          const updatedAllOffers = offers.map(offer => {
            const updatedOffer = updatedOffers.find(o => o.id === offer.id);
            return updatedOffer || offer;
          });

          updateSelectionData({ actionType: 'updateAllOffers', payload: updatedAllOffers });

          toast.success(
            <FormattedPluralMessage
              id="listing-manager.bulk-actions.image-enhancement.update-offers"
              count={updatedOffers.length}
            />,
          );
        },
        onError: error => {
          toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
        },
      });
    },
    [user, executeBatchUpdateOffers, selection.id, offers, updateSelectionData, FormattedPluralMessage],
  );

  return {
    bulkUpdateOfferImage,
  };
};
